import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  Package,
  Users,
  Settings,
  LogOut,
  Download,
  PlusCircle,
  QrCode,
  ChevronRight,
  Home,
  Search,
  Calendar,
  Eye,
  Smartphone,
  Laptop,
  Key,
  Watch,
  Headphones,
  Camera,
  // Ajoutez ces imports
  DollarSign,
  CreditCard,
  UserPlus,
  TrendingUp,
  ShoppingBag,
  CheckCircle,
  XCircle,
  RefreshCw,
  Filter,
  Link,
  Copy,
  Trash2,
  Clock,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import toast from "react-hot-toast";
import QRCode from "react-qr-code";
import "../../styles/AdminDashboard.css";

// Import Firebase
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  orderBy,
  limit,
  doc,
  updateDoc,
  serverTimestamp,
  getCountFromServer,
  onSnapshot,
} from "firebase/firestore";
import { exportUnassignedQRCodes } from "../../components/scan/exportpdf";
import { db } from "../../lib/firebase";

// Types d'objets disponibles
const OBJECT_TYPES = [
  { id: "phone", label: "Téléphone", icon: Smartphone },
  { id: "laptop", label: "Ordinateur portable", icon: Laptop },
  { id: "tablet", label: "Tablette", icon: Laptop },
  { id: "keys", label: "Clés", icon: Key },
  { id: "wallet", label: "Portefeuille", icon: Package },
  { id: "watch", label: "Montre", icon: Watch },
  { id: "headphones", label: "Écouteurs", icon: Headphones },
  { id: "camera", label: "Appareil photo", icon: Camera },
  { id: "bag", label: "Sac", icon: Package },
  { id: "other", label: "Autre", icon: Package },
];

// Types de packs disponibles
const PACKAGES = [
  { id: "basic", name: "Pack Basique", price: 6000, tags: 5 },
  { id: "standard", name: "Pack Standard", price: 10000, tags: 10 },
  { id: "premium", name: "Pack Premium", price: 15000, tags: 20 },
  { id: "custom", name: "Personnalisé", price: 0, tags: 0 },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [batchSize, setBatchSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  // Ajoutez après vos autres états
  const [showSaleDetailModal, setShowSaleDetailModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [stats, setStats] = useState({
    totalQRCodes: 0,
    assigned: 0,
    active: 0,
    found: 0,
  });

  const handleExportStickers = () => {
    exportUnassignedQRCodes(qrCodes, toast);
  };

  // Ajoutez ces états après vos autres états (vers le début du composant)
  const [qrFilterStatus, setQrFilterStatus] = useState("all"); // "all", "assigned", "unassigned", "scanned"
  const [currentQrPage, setCurrentQrPage] = useState(1);
  const [qrItemsPerPage] = useState(20); // 20 QR codes par page

  const [currentSalesPage, setCurrentSalesPage] = useState(1);
  const [salesItemsPerPage] = useState(15); // 15 ventes par page

  // Ajoutez ces états
  const [sales, setSales] = useState([]);
  const [loadingSales, setLoadingSales] = useState(true);
  const [showAddSaleForm, setShowAddSaleForm] = useState(false);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    paymentMethod: "all",
    status: "all",
  });

  // État pour le formulaire
  const [saleForm, setSaleForm] = useState({
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    paymentMethod: "cinetpay",
    amount: 0,
    package: "basic",
    customTags: 1,
    cinetpayId: "",
    status: "completed",
    notes: "",
  });

  // Ajoutez ces états après les autres
  const [showQRDetailModal, setShowQRDetailModal] = useState(false);
  const [selectedQRDetail, setSelectedQRDetail] = useState(null);

  const downloadQRAsPNG = (qrId) => {
    try {
      // Trouver l'élément SVG du QR code
      const svgElement = document.querySelector(`#qr-code-${qrId} svg`);
      if (!svgElement) {
        toast.error("QR code non trouvé");
        return;
      }

      // Convertir SVG en canvas
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // Définir la taille du canvas
      const size = 300; // Taille en pixels
      canvas.width = size;
      canvas.height = size;

      // Créer une image à partir du SVG
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const svgBlob = new Blob([svgData], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(svgBlob);

      const img = new Image();
      img.onload = () => {
        // Dessiner l'image sur le canvas
        ctx.drawImage(img, 0, 0, size, size);

        // Convertir le canvas en PNG
        const pngUrl = canvas.toDataURL("image/png");

        // Créer un lien de téléchargement
        const link = document.createElement("a");
        link.href = pngUrl;
        link.download = `ultimatag-qrcode-${qrId}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Libérer l'URL
        URL.revokeObjectURL(url);

        toast.success("QR code téléchargé en PNG");
      };

      img.onerror = () => {
        console.error("Erreur chargement image SVG");
        toast.error("Erreur lors du téléchargement");
        URL.revokeObjectURL(url);
      };

      img.src = url;
    } catch (error) {
      console.error("Erreur génération PNG:", error);
      toast.error("Erreur lors du téléchargement");
    }
  };

  const formatDate = (date) => {
    if (!date) return "Non disponible";
    return date.toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatSimpleDate = (date) => {
    if (!date) return "Non disponible";
    return date.toLocaleDateString("fr-FR");
  };

  const renderQRDetailModal = () => {
    if (!selectedQRDetail) return null;

    const {
      qrId,
      status,
      createdAt,
      firstScan,
      lastScan,
      scanCount,
      objectName,
      objectType,
      ownerEmail,
      ownerName,
      ownerPhone,
      isFound,
      isActive,
      generatedBy,
    } = selectedQRDetail;

    const ObjectIcon =
      OBJECT_TYPES.find((t) => t.id === objectType)?.icon || Package;

    return (
      <div className="modal-overlay">
        <div className="modal-content large">
          <div className="modal-header">
            <h3>Détails du QR Code</h3>
            <button
              onClick={() => {
                setShowQRDetailModal(false);
                setSelectedQRDetail(null);
              }}
              className="modal-close"
            >
              ×
            </button>
          </div>

          <div className="modal-body">
            <div className="qr-detail-content">
              {/* Section en-tête avec QR */}
              <div className="qr-detail-header">
                <div
                  className="qr-display-large"
                  id={`qr-code-${selectedQRDetail.qrId}`}
                >
                  <QRCode
                    value={`${window.location.origin}/scan/${selectedQRDetail.qrId}`}
                    size={180}
                    bgColor="#FFFFFF"
                    fgColor="#0066FF"
                  />
                </div>

                <div className="qr-info-main">
                  <div className="qr-id-large">{qrId}</div>
                  <div
                    className={`qr-status-large ${
                      status === "assigned"
                        ? "status-assigned"
                        : "status-unassigned"
                    }`}
                  >
                    {status === "assigned" ? "Assigné" : "Libre"}
                  </div>

                  <div className="qr-actions-row">
                    <button
                      onClick={() => handleViewAssignPage(qrId)}
                      className="action-btn primary"
                      disabled={status === "assigned"}
                    >
                      <Key size={16} />
                      Lien d'assignation
                    </button>

                    <button
                      onClick={() => handleViewScanPage(qrId)}
                      className="action-btn secondary"
                    >
                      <Eye size={16} />
                      Lien de scan
                    </button>

                    <button
                      onClick={() => handleResetQR(qrId)}
                      className="action-btn danger"
                      disabled={status !== "assigned"}
                    >
                      <RefreshCw size={16} />
                      Réinitialiser
                    </button>
                  </div>
                </div>
              </div>

              {/* Statistiques */}
              <div className="stats-grid-detail">
                <div className="stat-detail">
                  <div className="stat-label">Scans totaux</div>
                  <div className="stat-value">{scanCount || 0}</div>
                </div>

                <div className="stat-detail">
                  <div className="stat-label">Statut</div>
                  <div className={`stat-value status-${status}`}>
                    {status === "assigned" ? "Assigné" : "Libre"}
                  </div>
                </div>

                <div className="stat-detail">
                  <div className="stat-label">Objet retrouvé</div>
                  <div
                    className={`stat-value ${isFound ? "found" : "not-found"}`}
                  >
                    {isFound ? "Oui" : "Non"}
                  </div>
                </div>

                <div className="stat-detail">
                  <div className="stat-label">Actif</div>
                  <div
                    className={`stat-value ${isActive ? "active" : "inactive"}`}
                  >
                    {isActive ? "Oui" : "Non"}
                  </div>
                </div>
              </div>

              {/* Informations de l'objet */}
              {status === "assigned" && (
                <div className="detail-section">
                  <h4 className="section-title">
                    <ObjectIcon size={18} />
                    Informations de l'objet
                  </h4>

                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-label">Nom de l'objet:</span>
                      <span className="info-value">
                        {objectName || "Non spécifié"}
                      </span>
                    </div>

                    <div className="info-item">
                      <span className="info-label">Type d'objet:</span>
                      <span className="info-value">
                        {OBJECT_TYPES.find((t) => t.id === objectType)?.label ||
                          "Non spécifié"}
                      </span>
                    </div>

                    <div className="info-item">
                      <span className="info-label">Propriétaire:</span>
                      <span className="info-value">
                        {ownerName && ownerPhone
                          ? `${ownerName} - ${ownerPhone}`
                          : ownerName || ownerPhone || "Non spécifié"}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Historique des scans - Version simplifiée */}
              <div className="detail-section">
                <h4 className="section-title">
                  <Calendar size={18} />
                  Historique d'activité
                </h4>

                <div className="history-simple">
                  <div className="history-item">
                    <div className="history-icon">
                      <QrCode size={16} />
                    </div>
                    <div className="history-content">
                      <div className="history-title">QR code créé</div>
                      <div className="history-date">
                        {formatSimpleDate(createdAt)}
                      </div>
                    </div>
                  </div>

                  <div className="history-item">
                    <div className="history-icon">
                      <Key size={16} />
                    </div>
                    <div className="history-content">
                      <div className="history-title">Statut d'assignation</div>
                      <div className="history-detail">
                        {status === "assigned" ? (
                          <span className="status-badge assigned">Assigné</span>
                        ) : (
                          <span className="status-badge unassigned">
                            Non assigné
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="history-item">
                    <div className="history-icon">
                      <Eye size={16} />
                    </div>
                    <div className="history-content">
                      <div className="history-title">Nombre de scans</div>
                      <div className="history-detail">
                        <span className="scan-count">
                          {scanCount || 0} scan{scanCount !== 1 ? "s" : ""}
                        </span>
                      </div>
                    </div>
                  </div>

                  {firstScan && (
                    <div className="history-item">
                      <div className="history-icon">
                        <Calendar size={16} />
                      </div>
                      <div className="history-content">
                        <div className="history-title">Premier scan</div>
                        <div className="history-date">
                          {formatSimpleDate(firstScan)}
                        </div>
                      </div>
                    </div>
                  )}

                  {lastScan && (
                    <div className="history-item">
                      <div className="history-icon">
                        <Calendar size={16} />
                      </div>
                      <div className="history-content">
                        <div className="history-title">Dernier scan</div>
                        <div className="history-date">
                          {formatSimpleDate(lastScan)}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="history-item">
                    <div className="history-icon">
                      <CheckCircle size={16} />
                    </div>
                    <div className="history-content">
                      <div className="history-title">Objet retrouvé</div>
                      <div className="history-detail">
                        {isFound ? (
                          <span className="status-badge found">✅ Oui</span>
                        ) : (
                          <span className="status-badge not-found">❌ Non</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* URLs importantes */}
              <div className="detail-section">
                <h4 className="section-title">
                  <Link size={18} />
                  URLs importantes
                </h4>

                <div className="url-list">
                  <div className="url-item">
                    <span className="url-label">Lien d'assignation:</span>
                    <a
                      href={`${window.location.origin}/assign/${qrId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="url-link"
                    >
                      {`${window.location.origin}/assign/${qrId}`}
                    </a>
                  </div>

                  <div className="url-item">
                    <span className="url-label">Lien de scan public:</span>
                    <a
                      href={`${window.location.origin}/scan/${qrId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="url-link"
                    >
                      {`${window.location.origin}/scan/${qrId}`}
                    </a>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="detail-section actions-section">
                <h4 className="section-title">
                  <Settings size={18} />
                  Actions
                </h4>

                <div className="action-buttons-grid">
                  <button
                    className="action-btn download-btn"
                    onClick={() => downloadQRAsPNG(selectedQRDetail.qrId)}
                  >
                    <Download size={16} />
                    Télécharger PNG
                  </button>
                  <button
                    className="action-btn copy-btn"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        `${window.location.origin}/assign/${qrId}`
                      );
                      toast.success("Lien copié dans le presse-papier");
                    }}
                  >
                    <Copy size={16} />
                    Copier lien assignation
                  </button>

                  <button
                    className="action-btn copy-btn"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        `${window.location.origin}/scan/${qrId}`
                      );
                      toast.success("Lien copié dans le presse-papier");
                    }}
                  >
                    <Copy size={16} />
                    Copier lien scan
                  </button>

                  <button
                    className="action-btn export-btn"
                    onClick={() => {
                      const exportData = {
                        qrId,
                        status: status === "assigned" ? "Assigné" : "Libre",
                        createdAt: formatDate(createdAt),
                        firstScan: formatDate(firstScan),
                        lastScan: formatDate(lastScan),
                        scanCount,
                        objectName,
                        objectType: OBJECT_TYPES.find(
                          (t) => t.id === objectType
                        )?.label,
                        ownerEmail,
                        isFound: isFound ? "Oui" : "Non",
                        assignUrl: `${window.location.origin}/assign/${qrId}`,
                        scanUrl: `${window.location.origin}/scan/${qrId}`,
                      };

                      const json = JSON.stringify(exportData, null, 2);
                      const blob = new Blob([json], {
                        type: "application/json",
                      });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement("a");
                      link.href = url;
                      link.download = `qrcode-${qrId}.json`;
                      link.click();

                      toast.success("Données exportées en JSON");
                    }}
                  >
                    <Download size={16} />
                    Exporter en JSON
                  </button>

                  {status === "assigned" && (
                    <button
                      className="action-btn danger-btn"
                      onClick={() => {
                        if (
                          window.confirm(
                            "Êtes-vous sûr de vouloir réinitialiser ce QR code ? Toutes les données d'assignation seront perdues."
                          )
                        ) {
                          handleResetQR(qrId);
                          setShowQRDetailModal(false);
                        }
                      }}
                    >
                      <Trash2 size={16} />
                      Réinitialiser le QR code
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              onClick={() => {
                setShowQRDetailModal(false);
                setSelectedQRDetail(null);
              }}
              className="btn btn-outline"
            >
              Fermer
            </button>

            <button
              onClick={() => window.open(`/assign/${qrId}`, "_blank")}
              className="btn btn-primary"
            >
              Ouvrir la page d'assignation
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleViewQRDetail = (qr) => {
    setSelectedQRDetail(qr);
    setShowQRDetailModal(true);
  };

  // Ajoutez après handleViewQRDetail
  const handleViewSaleDetail = (sale) => {
    setSelectedSale(sale);
    setShowSaleDetailModal(true);
  };

  useEffect(() => {
    if (!user || user.role !== "admin") {
      navigate("/admin/login");
      return;
    }

    const unsubscribeQR = loadData();
    const unsubscribeSales = loadSalesRealtime();

    // Cleanup function
    return () => {
      if (unsubscribeQR) unsubscribeQR();
      if (unsubscribeSales) unsubscribeSales();
    };
  }, [user]);

  const loadData = () => {
    setLoading(true);

    // Charger les statistiques
    loadQRStats();

    // Charger les QR codes avec subscription en temps réel
    const unsubscribe = loadQRCodesRealtime();

    setLoading(false);

    return unsubscribe;
  };

  // Ajoutez un cache pour les stats
  const [lastStatsUpdate, setLastStatsUpdate] = useState(null);
  const STATS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  const loadQRStats = async () => {
    // Vérifier si le cache est encore valide
    if (
      lastStatsUpdate &&
      Date.now() - lastStatsUpdate < STATS_CACHE_DURATION
    ) {
      return; // Utiliser les stats en cache
    }

    try {
      // Votre logique existante
      setLastStatsUpdate(Date.now());
    } catch (error) {
      console.error("Error loading stats:", error);
      // Utiliser les données locales
    }
  };

  const loadQRCodesRealtime = () => {
    try {
      const qrCodesQuery = query(
        collection(db, "qrCodes"),
        orderBy("createdAt", "desc")
      );

      const unsubscribe = onSnapshot(qrCodesQuery, (snapshot) => {
        const qrData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            qrId: data.qrId || data.id || doc.id,
            status: data.status || "unassigned",
            createdAt: data.createdAt?.toDate?.() || new Date(),
            lastScan: data.lastScan?.toDate?.() || null,
            firstScan: data.firstScan?.toDate?.() || null,
            scanCount: data.scanCount || 0,
            objectName: data.objectName || null,
            objectType: data.objectType || null,
            ownerId: data.ownerId || null,
            ownerEmail: data.ownerEmail || null,
            ownerPhone: data.ownerPhone || null,
            ownerName: data.ownerName || null,
            isFound: data.isFound || false,
            isActive: data.isActive !== false,
            generatedBy: data.generatedBy || null,
          };
        });

        setQrCodes(qrData);
        console.log("QR codes chargés:", qrData.length);
      });

      return unsubscribe;
    } catch (error) {
      console.error("Error loading QR codes:", error);
      toast.error("Erreur chargement QR codes");
      return () => {}; // Return empty cleanup function
    }
  };

  // Chargez les ventes depuis la collection "ventes"
  const loadSalesRealtime = () => {
    try {
      console.log(
        '🔄 Chargement des commandes depuis Firestore (collection "ventes")...'
      );

      const ordersQuery = query(
        collection(db, "ventes"),
        orderBy("dateCreation", "desc")
      );

      const unsubscribe = onSnapshot(
        ordersQuery,
        (snapshot) => {
          console.log(`📊 Commandes reçues: ${snapshot.docs.length}`);

          const ordersData = snapshot.docs.map((doc) => {
            const data = doc.data();

            return {
              id: doc.id,
              ...data,
              // Conversion des timestamps Firebase
              createdAt:
                data.dateCreation?.toDate?.() ||
                new Date(data.dateCreationString || Date.now()),
              updatedAt: data.dateMiseAJour?.toDate?.() || new Date(),

              // Mapping des champs pour l'affichage
              transactionId: data.transactionId || doc.id,
              customerName:
                data.client?.nomComplet || data.clientNom || "Client",
              customerEmail: data.client?.email || data.clientEmail || "",
              customerPhone:
                data.client?.telephone || data.clientTelephone || "",

              // Dans loadSalesRealtime, ajoutez dans le mapping:
              deliveryStatus:
                data.livraison?.statut || data.livraisonStatut || "EN_ATTENTE",
              deliveryDate:
                data.livraison?.dateLivraison?.toDate?.() ||
                data.dateLivraison ||
                null,

              // Détails du pack
              package: data.pack?.id || data.packId || "premium",
              packageName: data.pack?.nom || data.packNom || "Pack Premium",
              tagsCount: data.pack?.quantite || data.quantite || 1,

              // Montant
              amount: data.montant || data.totaux?.total || 0,

              // Statut - IMPORTANT: utiliser statutPaiement
              status: (
                data.statutPaiement ||
                data.status ||
                "PENDING"
              ).toLowerCase(),

              // Paiement
              paymentMethod:
                data.paiement?.methode ||
                data.methodePaiement ||
                "mobile_money",

              // Livraison
              deliveryAddress:
                data.livraison?.adresse || data.adresseLivraison || "",
              deliveryCity: data.livraison?.ville || data.villeLivraison || "",
              deliveryZone: data.livraison?.zone || data.zoneLivraison || "",
              deliveryCost: data.livraison?.frais || data.fraisLivraison || 0,

              // Type de produit
              productType: "physical_stickers",

              // Admin
              adminEmail:
                data.metadata?.source === "web" ? "Paiement en ligne" : "Admin",
            };
          });

          console.log(`✅ Commandes chargées: ${ordersData.length}`);
          setSales(ordersData);
          setLoadingSales(false);
        },
        (error) => {
          console.error("❌ Erreur onSnapshot:", error);
          setLoadingSales(false);
        }
      );

      return unsubscribe;
    } catch (error) {
      console.error("❌ Error loading orders:", error);
      setLoadingSales(false);
      return () => {};
    }
  };

  // Ajoutez cette fonction après renderQRDetailModal
  const renderSaleDetailModal = () => {
    if (!selectedSale) return null;

    const formatDate = (date) => {
      if (!date) return "Non disponible";
      if (date.toDate) date = date.toDate();
      return new Date(date).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    const formatMontant = (montant) => {
      return (montant || 0).toLocaleString("fr-FR") + " CFA";
    };

    const getStatusBadge = (status) => {
      const statusMap = {
        completed: { class: "status-completed", label: "Complété" },
        pending: { class: "status-pending", label: "En attente" },
        failed: { class: "status-failed", label: "Échoué" },
        cancelled: { class: "status-cancelled", label: "Annulé" },
        refunded: { class: "status-refunded", label: "Remboursé" },
        SUCCESS: { class: "status-completed", label: "Complété" },
        PENDING: { class: "status-pending", label: "En attente" },
        PAYEE: { class: "status-completed", label: "Payé" },
      };
      const statusInfo = statusMap[status] || {
        class: "status-unknown",
        label: status,
      };
      return (
        <span className={`sale-status-badge ${statusInfo.class}`}>
          {statusInfo.label}
        </span>
      );
    };

    const getDeliveryBadge = (statut) => {
      if (statut === "LIVREE") {
        return <span className="delivery-badge delivered">✅ Livrée</span>;
      }
      return <span className="delivery-badge pending">⏳ En attente</span>;
    };

    return (
      <div
        className="modal-overlay"
        onClick={() => setShowSaleDetailModal(false)}
      >
        <div
          className="modal-content sale-detail-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h3>Détails de la vente</h3>
            <button
              onClick={() => setShowSaleDetailModal(false)}
              className="modal-close-btn"
            >
              ×
            </button>
          </div>

          <div className="modal-body">
            {/* En-tête avec statuts */}
            <div className="detail-header">
              <div className="sale-id">
                <span className="label">Transaction:</span>
                <code className="value">{selectedSale.transactionId}</code>
              </div>
              <div className="status-group">
                <div className="status-item">
                  <span className="label">Paiement:</span>
                  {getStatusBadge(selectedSale.status)}
                </div>
                <div className="status-item">
                  <span className="label">Livraison:</span>
                  {getDeliveryBadge(
                    selectedSale.deliveryStatus || "EN_ATTENTE"
                  )}
                </div>
              </div>
            </div>

            {/* Informations client */}
            <div className="detail-section">
              <h4 className="section-title">
                <Users size={18} />
                Client
              </h4>
              <div className="detail-grid">
                <div className="detail-row">
                  <span className="detail-label">Nom:</span>
                  <span className="detail-value">
                    {selectedSale.customerName}
                  </span>
                </div>
                {selectedSale.customerEmail && (
                  <div className="detail-row">
                    <span className="detail-label">Email:</span>
                    <span className="detail-value">
                      {selectedSale.customerEmail}
                    </span>
                  </div>
                )}
                {selectedSale.customerPhone && (
                  <div className="detail-row">
                    <span className="detail-label">Téléphone:</span>
                    <span className="detail-value">
                      {selectedSale.customerPhone}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Adresse de livraison */}
            <div className="detail-section">
              <h4 className="section-title">
                <Package size={18} />
                Livraison
              </h4>
              <div className="detail-grid">
                <div className="detail-row">
                  <span className="detail-label">Ville:</span>
                  <span className="detail-value">
                    {selectedSale.deliveryCity || "Non spécifiée"}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Quartier:</span>
                  <span className="detail-value">
                    {selectedSale.deliveryZone || "Non spécifié"}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Adresse:</span>
                  <span className="detail-value">
                    {selectedSale.deliveryAddress || "Non spécifiée"}
                  </span>
                </div>
                {selectedSale.deliveryNotes && (
                  <div className="detail-row">
                    <span className="detail-label">Instructions:</span>
                    <span className="detail-value">
                      {selectedSale.deliveryNotes}
                    </span>
                  </div>
                )}
                <div className="detail-row">
                  <span className="detail-label">Frais:</span>
                  <span className="detail-value">
                    {formatMontant(selectedSale.deliveryCost)}
                  </span>
                </div>
                {selectedSale.deliveryDate && (
                  <div className="detail-row">
                    <span className="detail-label">Date livraison:</span>
                    <span className="detail-value">
                      {formatDate(selectedSale.deliveryDate)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Détails de la commande */}
            <div className="detail-section">
              <h4 className="section-title">
                <ShoppingBag size={18} />
                Commande
              </h4>
              <div className="detail-grid">
                <div className="detail-row">
                  <span className="detail-label">Pack:</span>
                  <span className="detail-value">
                    {selectedSale.packageName || "Pack"}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Tags:</span>
                  <span className="detail-value">
                    {selectedSale.tagsCount || 0}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Sous-total:</span>
                  <span className="detail-value">
                    {formatMontant(
                      selectedSale.amount - (selectedSale.deliveryCost || 0)
                    )}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Livraison:</span>
                  <span className="detail-value">
                    {formatMontant(selectedSale.deliveryCost)}
                  </span>
                </div>
                <div className="detail-row total">
                  <span className="detail-label">Total:</span>
                  <span className="detail-value">
                    {formatMontant(selectedSale.amount)}
                  </span>
                </div>
              </div>
            </div>

            {/* Métadonnées */}
            <div className="detail-section">
              <h4 className="section-title">
                <Calendar size={18} />
                Dates
              </h4>
              <div className="detail-grid">
                <div className="detail-row">
                  <span className="detail-label">Création:</span>
                  <span className="detail-value">
                    {formatDate(selectedSale.createdAt)}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Paiement:</span>
                  <span className="detail-value">
                    {selectedSale.paymentMethod || "Non spécifié"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              onClick={() => setShowSaleDetailModal(false)}
              className="btn btn-outline"
            >
              Fermer
            </button>

            {/* Bouton Marquer comme livré - visible seulement si non livré */}
            {selectedSale.deliveryStatus !== "LIVREE" && (
              <button
                onClick={() =>
                  handleMarkAsDelivered(
                    selectedSale.id,
                    selectedSale.transactionId
                  )
                }
                className="btn btn-success"
              >
                <CheckCircle size={16} />
                Marquer comme livré
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };
  const calculateSalesStats = () => {
    const today = new Date();
    const lastMonth = new Date();
    lastMonth.setMonth(lastMonth.getMonth() - 1);

    // 1. Filtrer uniquement les ventes COMPLETED (statut 'completed' ou 'SUCCESS')
    const completedSales = sales.filter(
      (sale) =>
        sale.status === "completed" ||
        sale.status === "SUCCESS" ||
        sale.status === "PAYEE"
    );

    // 2. Filtrer les ventes PENDING
    const pendingSales = sales.filter(
      (sale) => sale.status === "pending" || sale.status === "PENDING"
    );

    // 3. TOTAL REVENU - Uniquement les ventes completed
    const totalRevenue = completedSales.reduce(
      (sum, sale) => sum + (sale.amount || 0),
      0
    );

    // 4. REVENU MENSUEL - Uniquement les ventes completed du dernier mois
    const monthlyRevenue = completedSales
      .filter((sale) => sale.createdAt >= lastMonth)
      .reduce((sum, sale) => sum + (sale.amount || 0), 0);

    // 5. TAGS VENDUS - SOMME DU CHAMP quantite des ventes complétées
    const totalTagsSold = completedSales.reduce((sum, sale) => {
      // Utiliser quantite (champ présent dans vos données Firebase)
      const quantite =
        sale.quantite || // Champ direct
        sale.pack?.quantite || // Dans l'objet pack
        sale.tagsCount || // Fallback
        0;

      console.log(`Vente ${sale.transactionId}: quantite = ${quantite}`); // Debug
      return sum + quantite;
    }, 0);

    // 6. STATISTIQUES PAR PACK - Uniquement les ventes completed
    const packageStats = PACKAGES.map((pkg) => {
      const packageSales = completedSales.filter(
        (sale) =>
          sale.package === pkg.id ||
          sale.packId === pkg.id ||
          sale.pack?.id === pkg.id
      );

      const revenue = packageSales.reduce(
        (sum, sale) => sum + (sale.amount || 0),
        0
      );
      const count = packageSales.length;

      // Somme des quantites pour ce pack
      const tagsSold = packageSales.reduce((sum, sale) => {
        const quantite =
          sale.quantite || sale.pack?.quantite || sale.tagsCount || 0;
        return sum + quantite;
      }, 0);

      return {
        ...pkg,
        revenue,
        count,
        tagsSold,
      };
    });

    // 7. DISTRIBUTION DES STATUTS (toutes les ventes)
    const statusCounts = {
      completed: sales.filter(
        (s) =>
          s.status === "completed" ||
          s.status === "SUCCESS" ||
          s.status === "PAYEE"
      ).length,
      pending: sales.filter(
        (s) => s.status === "pending" || s.status === "PENDING"
      ).length,
      failed: sales.filter(
        (s) =>
          s.status === "failed" ||
          s.status === "FAILED" ||
          s.status === "ECHOUE"
      ).length,
      cancelled: sales.filter(
        (s) =>
          s.status === "cancelled" ||
          s.status === "CANCELLED" ||
          s.status === "ANNULE"
      ).length,
    };

    // 8. VENTES EN PENDING - Montant total des ventes en attente
    const pendingRevenue = pendingSales.reduce(
      (sum, sale) => sum + (sale.amount || 0),
      0
    );

    // 9. TAUX DE CONVERSION (ventes completed / total des ventes)
    const conversionRate =
      sales.length > 0
        ? ((statusCounts.completed / sales.length) * 100).toFixed(1)
        : 0;

    // 10. VALEUR MOYENNE D'UNE VENTE COMPLETED
    const averageOrderValue =
      statusCounts.completed > 0
        ? (totalRevenue / statusCounts.completed).toFixed(0)
        : 0;

    // 11. VALEUR MOYENNE PAR TAG
    const averageValuePerTag =
      totalTagsSold > 0 ? (totalRevenue / totalTagsSold).toFixed(0) : 0;

    console.log("📊 Statistiques calculées:", {
      totalRevenue,
      completedSales: statusCounts.completed,
      totalTagsSold,
      averageValuePerTag,
      packageStats,
    });

    return {
      // Totaux financiers (uniquement completed)
      totalRevenue,
      monthlyRevenue,
      averageOrderValue: parseInt(averageOrderValue),
      averageValuePerTag: parseInt(averageValuePerTag),

      // Ventes en pending
      pendingSalesCount: statusCounts.pending,
      pendingRevenue,

      // Totaux quantitatifs (uniquement completed)
      totalTagsSold, // ✅ MAINTENANT C'EST LA SOMME DE quantite

      // Nombre de ventes
      totalSales: sales.length,
      completedSales: statusCounts.completed,
      pendingSales: statusCounts.pending,
      failedSales: statusCounts.failed,
      cancelledSales: statusCounts.cancelled,

      // Statistiques détaillées (uniquement completed)
      packageStats,
      statusCounts,

      // Taux
      conversionRate: parseFloat(conversionRate),
    };
  };
  const generateQRCodeId = () => {
    const timestamp = Date.now().toString(36).toUpperCase();
    const random = Math.random().toString(36).substring(2, 7).toUpperCase();
    return `UT${timestamp}${random}`;
  };

  const handleGenerateQR = async () => {
    if (!batchSize) {
      toast.error("Nombre invalide (1-100)");
      return;
    }

    if (!user?.uid) {
      toast.error("Non authentifié");
      return;
    }

    setGenerating(true);

    try {
      const batch = [];

      for (let i = 0; i < batchSize; i++) {
        const qrId = generateQRCodeId();

        const qrData = {
          qrId,
          status: "unassigned",
          generatedBy: user.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          isActive: true,
          scanCount: 0,
          isFound: false,
          // URL pour le premier scan (assignation)
          assignUrl: `${window.location.origin}/assign/${qrId}`,
        };

        batch.push(addDoc(collection(db, "qrCodes"), qrData));
      }

      await Promise.all(batch);

      // Recharger les statistiques
      await loadQRStats();

      toast.success(`${batchSize} QR codes générés`);
    } catch (error) {
      console.error("Erreur génération QR:", error);
      toast.error("Erreur lors de la génération");
    } finally {
      setGenerating(false);
    }
  };

  const handleExport = () => {
    const exportData = qrCodes.map((qr) => ({
      qrId: qr.qrId,
      status: qr.status,
      createdAt: qr.createdAt.toLocaleDateString("fr-FR"),
      firstScan: qr.firstScan
        ? qr.firstScan.toLocaleDateString("fr-FR")
        : "Non assigné",
      lastScan: qr.lastScan
        ? qr.lastScan.toLocaleDateString("fr-FR")
        : "Jamais",
      scanCount: qr.scanCount,
      objectName: qr.objectName || "Non assigné",
      objectType: qr.objectType || "Non spécifié",
      ownerEmail: qr.ownerEmail || "Non assigné",
      isFound: qr.isFound ? "Oui" : "Non",
      assignUrl: `${window.location.origin}/assign/${qr.qrId}`,
    }));

    const csvContent = [
      [
        "ID QR",
        "Statut",
        "Date création",
        "Premier scan",
        "Dernier scan",
        "Nombre scans",
        "Nom objet",
        "Type objet",
        "Propriétaire",
        "Retrouvé",
        "URL Assignation",
      ],
      ...exportData.map((item) => [
        item.qrId,
        item.status,
        item.createdAt,
        item.firstScan,
        item.lastScan,
        item.scanCount,
        item.objectName,
        item.objectType,
        item.ownerEmail,
        item.isFound,
        item.assignUrl,
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `ultimatag-qrcodes-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    link.click();

    toast.success("Export CSV terminé");
  };

  const handleViewAssignPage = (qrId) => {
    // Ouvrir la page d'assignation dans un nouvel onglet
    window.open(`/assign/${qrId}`, "_blank");
  };

  const handleViewScanPage = (qrId) => {
    // Ouvrir la page de scan dans un nouvel onglet
    window.open(`/scan/${qrId}`, "_blank");
  };

  // Ajoutez cette fonction dans le composant AdminDashboard
  const verifyPendingPayments = async () => {
    if (
      !confirm(
        "Voulez-vous vérifier tous les paiements en attente ? Cette opération peut prendre quelques minutes."
      )
    ) {
      return;
    }

    toast.loading("Vérification des paiements en cours...", {
      id: "verify-payments",
    });

    try {
      // Récupérer toutes les ventes avec statut 'pending'
      const pendingSales = sales.filter(
        (sale) => sale.status === "pending" || sale.status === "PENDING"
      );

      console.log(`🔍 ${pendingSales.length} paiements en attente à vérifier`);

      let verified = 0;
      let updated = 0;
      let failed = 0;
      let cancelled = 0;

      // Date d'il y a 24h
      const oneDayAgo = new Date();
      oneDayAgo.setDate(oneDayAgo.getDate() - 1);

      for (const sale of pendingSales) {
        verified++;

        // Vérifier si la transaction date de plus de 24h
        const saleDate =
          sale.createdAt instanceof Date
            ? sale.createdAt
            : new Date(sale.createdAt);
        const isOld = saleDate < oneDayAgo;

        if (isOld) {
          // Marquer comme annulé si > 24h
          console.log(
            `⏰ Transaction ${sale.transactionId} datant de plus de 24h - Annulation`
          );

          try {
            // Mettre à jour dans Firebase
            const q = query(
              collection(db, "ventes"),
              where("transactionId", "==", sale.transactionId),
              limit(1)
            );
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
              const venteDoc = querySnapshot.docs[0];
              const venteRef = doc(db, "ventes", venteDoc.id);

              await updateDoc(venteRef, {
                statutPaiement: "CANCELLED",
                status: "CANCELLED",
                dateAnnulation: serverTimestamp(),
                motifAnnulation: "Automatique - Plus de 24h sans confirmation",
              });

              cancelled++;
            }
          } catch (updateError) {
            console.error("Erreur annulation:", updateError);
          }

          continue;
        }

        // Vérifier via l'API CinetPay
        try {
          console.log(`🔄 Vérification CinetPay: ${sale.transactionId}`);

          const response = await fetch(
            "https://api-checkout.cinetpay.com/v2/payment/check",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                apikey:
                  import.meta.env.VITE_CINETPAY_API_KEY ||
                  "2699980936909bf80755699.80037643",
                site_id: import.meta.env.VITE_CINETPAY_SITE_ID || "105906962",
                transaction_id: sale.transactionId,
              }),
            }
          );

          const result = await response.json();
          console.log(`📊 Résultat pour ${sale.transactionId}:`, result);

          if (result.code === "00" && result.data) {
            let newStatus = "pending";

            switch (result.data.status) {
              case "ACCEPTED":
              case "SUCCESS":
                newStatus = "completed";
                break;
              case "REFUSED":
              case "FAILED":
                newStatus = "failed";
                break;
              case "PENDING":
                newStatus = "pending";
                break;
              default:
                newStatus = "pending";
            }

            // Si le statut a changé, mettre à jour Firebase
            if (newStatus !== sale.status) {
              console.log(
                `✅ Mise à jour ${sale.transactionId}: ${sale.status} -> ${newStatus}`
              );

              const q = query(
                collection(db, "ventes"),
                where("transactionId", "==", sale.transactionId),
                limit(1)
              );
              const querySnapshot = await getDocs(q);

              if (!querySnapshot.empty) {
                const venteDoc = querySnapshot.docs[0];
                const venteRef = doc(db, "ventes", venteDoc.id);

                await updateDoc(venteRef, {
                  statutPaiement: newStatus.toUpperCase(),
                  status: newStatus.toUpperCase(),
                  dateVerification: serverTimestamp(),
                  "paiement.statut": newStatus.toUpperCase(),
                  "paiement.dateVerification": serverTimestamp(),
                });

                if (newStatus === "completed") {
                  updated++;
                } else if (newStatus === "failed") {
                  failed++;
                }
              }
            }
          }
        } catch (apiError) {
          console.error(`❌ Erreur API pour ${sale.transactionId}:`, apiError);
        }

        // Petite pause pour éviter de surcharger l'API
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      toast.dismiss("verify-payments");
      toast.success(
        `Vérification terminée: ${verified} traités, ${updated} mis à jour, ${failed} échoués, ${cancelled} annulés (24h+)`
      );
    } catch (error) {
      console.error("❌ Erreur vérification globale:", error);
      toast.dismiss("verify-payments");
      toast.error("Erreur lors de la vérification");
    }
  };
  const handleResetQR = async (qrId) => {
    if (
      !confirm(
        "Voulez-vous vraiment réinitialiser ce QR code ? Toutes les données d'assignation seront perdues."
      )
    ) {
      return;
    }

    try {
      const qrDoc = qrCodes.find((qr) => qr.id === qrId || qr.qrId === qrId);
      if (!qrDoc) {
        toast.error("QR code non trouvé");
        return;
      }

      const qrRef = doc(db, "qrCodes", qrDoc.id);
      await updateDoc(qrRef, {
        status: "unassigned",
        objectName: null,
        objectType: null,
        ownerId: null,
        ownerEmail: null,
        firstScan: null,
        lastScan: null,
        scanCount: 0,
        isFound: false,
        updatedAt: serverTimestamp(),
      });

      toast.success("QR code réinitialisé avec succès");
    } catch (error) {
      console.error("Error resetting QR:", error);
      toast.error("Erreur lors de la réinitialisation");
    }
  };

  // Ajoutez après handleResetQR
  const handleMarkAsDelivered = async (saleId, transactionId) => {
    try {
      // Demander confirmation
      if (!confirm("Confirmez-vous que cette commande a été livrée ?")) {
        return;
      }

      // Mettre à jour dans Firebase
      const saleRef = doc(db, "ventes", saleId);
      await updateDoc(saleRef, {
        "livraison.statut": "LIVREE",
        livraisonStatut: "LIVREE",
        dateLivraison: serverTimestamp(),
        dateLivraisonString: new Date().toISOString(),
      });

      toast.success("Commande marquée comme livrée");

      // Fermer le modal
      setShowSaleDetailModal(false);
      setSelectedSale(null);
    } catch (error) {
      console.error("Erreur lors du marquage de livraison:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleAddSale = async () => {
    try {
      // Validation
      if (!saleForm.customerName || !saleForm.amount || saleForm.amount <= 0) {
        toast.error("Veuillez remplir tous les champs obligatoires");
        return;
      }

      // Validation des champs de livraison
      if (
        !saleForm.deliveryCity ||
        !saleForm.deliveryZone ||
        !saleForm.deliveryAddress
      ) {
        toast.error("Veuillez remplir tous les champs de livraison");
        return;
      }

      // Calcul du nombre de tags
      let tagsCount = 0;
      if (saleForm.package === "custom") {
        tagsCount = saleForm.customTags;
      } else {
        const pkg = PACKAGES.find((p) => p.id === saleForm.package);
        tagsCount = pkg ? pkg.tags : 0;
      }

      // Montant total incluant les frais de livraison
      const totalAmount = saleForm.amount + (saleForm.deliveryCost || 0);

      // Générer un ID de transaction unique
      const transactionId = `TXN${Date.now()
        .toString(36)
        .toUpperCase()}${Math.random()
        .toString(36)
        .substring(2, 6)
        .toUpperCase()}`;

      // Date actuelle pour l'historique (pas de serverTimestamp dans le tableau)
      const now = new Date();
      const nowISO = now.toISOString();

      // Structure correspondant à votre collection "ventes"
      const saleData = {
        livraisonStatut: "EN_ATTENTE",
        // Transaction
        transactionId: transactionId,
        montant: totalAmount,
        dateCreation: serverTimestamp(), // OK ici (pas dans un tableau)
        dateCreationString: nowISO,

        // Client (champs plats)
        clientNom: saleForm.customerName,
        clientEmail: saleForm.customerEmail || "",
        clientTelephone: saleForm.customerPhone || "",

        // Client structuré
        client: {
          nomComplet: saleForm.customerName,
          email: saleForm.customerEmail || "",
          telephone: saleForm.customerPhone || "",
          adresse: saleForm.deliveryAddress || "",
          ville: saleForm.deliveryCity || "",
          zone: saleForm.deliveryZone || "",
        },

        // Pack
        packId: saleForm.package,
        packNom:
          PACKAGES.find((p) => p.id === saleForm.package)?.name ||
          "Pack personnalisé",
        quantite: tagsCount,

        // Pack structuré
        pack: {
          id: saleForm.package,
          nom:
            PACKAGES.find((p) => p.id === saleForm.package)?.name ||
            "Pack personnalisé",
          quantite: tagsCount,
          prixUnitaire:
            saleForm.package === "custom"
              ? Math.round(saleForm.amount / tagsCount)
              : PACKAGES.find((p) => p.id === saleForm.package)?.price || 0,
        },

        // LIVRAISON
        adresseLivraison: saleForm.deliveryAddress || "",
        villeLivraison: saleForm.deliveryCity || "",
        zoneLivraison: saleForm.deliveryZone || "",
        fraisLivraison: saleForm.deliveryCost || 0,
        instructionsLivraison: saleForm.deliveryNotes || "",

        // Livraison structurée
        livraison: {
          adresse: saleForm.deliveryAddress || "",
          ville: saleForm.deliveryCity || "",
          zone: saleForm.deliveryZone || "",
          frais: saleForm.deliveryCost || 0,
          instructions: saleForm.deliveryNotes || "",
          statut: "EN_ATTENTE", // Ajouté
          dateLivraison: null, // Pour stocker la date de livraison
        },

        // Paiement
        methodePaiement: saleForm.paymentMethod,
        operateurPaiement: null,
        statutPaiement: saleForm.status.toUpperCase(),

        // Paiement structuré
        paiement: {
          methode: saleForm.paymentMethod,
          operateur: null,
          statut: saleForm.status.toUpperCase(),
          transactionId: transactionId,
          reference: saleForm.cinetpayId || null,
        },

        // Totaux
        totaux: {
          sousTotal: saleForm.amount,
          fraisLivraison: saleForm.deliveryCost || 0,
          total: totalAmount,
          devise: "XOF",
        },

        // Métadonnées
        metadata: {
          source: "admin",
          dateCreation: nowISO,
          notes: saleForm.notes || "",
          adminId: user.uid,
          adminEmail: user.email,
        },

        // Statut
        status: saleForm.status.toUpperCase(),
        statutCommande:
          saleForm.status === "completed"
            ? "PAYEE"
            : saleForm.status === "pending"
            ? "EN_ATTENTE_PAIEMENT"
            : saleForm.status.toUpperCase(),

        // HISTORIQUE - CORRIGÉ : utiliser une date ISO string au lieu de serverTimestamp()
        historiqueStatuts: [
          {
            statut: saleForm.status.toUpperCase(),
            date: nowISO, // Utiliser une string ISO au lieu de serverTimestamp()
            commentaire: `Vente créée manuellement par ${user.email}`,
          },
        ],

        // Admin
        adminId: user.uid,
        adminEmail: user.email,
      };

      console.log("📤 Données vente à sauvegarder:", saleData);

      // Utiliser la collection "ventes"
      await addDoc(collection(db, "ventes"), saleData);

      toast.success("Vente enregistrée avec succès");
      setShowAddSaleForm(false);
      resetSaleForm();
    } catch (error) {
      console.error("❌ Erreur ajout vente:", error);
      toast.error("Erreur lors de l'enregistrement: " + error.message);
    }
  };
  const resetSaleForm = () => {
    setSaleForm({
      customerName: "",
      customerEmail: "",
      customerPhone: "",
      deliveryCity: "", // Nouveau
      deliveryZone: "", // Nouveau
      deliveryAddress: "", // Nouveau
      deliveryCost: 0, // Nouveau
      deliveryNotes: "", // Nouveau
      paymentMethod: "cinetpay",
      amount: 0,
      package: "basic",
      customTags: 1,
      cinetpayId: "",
      status: "completed",
      notes: "",
    });
  };

  const handlePackageChange = (packageId) => {
    setSaleForm((prev) => {
      const pkg = PACKAGES.find((p) => p.id === packageId);
      return {
        ...prev,
        package: packageId,
        amount: pkg && pkg.id !== "custom" ? pkg.price : prev.amount,
      };
    });
  };

  // Filtrer les ventes
  const filteredSales = sales.filter((sale) => {
    if (filters.startDate && sale.createdAt < new Date(filters.startDate))
      return false;
    if (filters.endDate && sale.createdAt > new Date(filters.endDate))
      return false;
    if (
      filters.paymentMethod !== "all" &&
      sale.paymentMethod !== filters.paymentMethod
    )
      return false;
    if (filters.status !== "all" && sale.status !== filters.status)
      return false;
    return true;
  });

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/admin/login");
    } catch (error) {
      console.error("Erreur déconnexion:", error);
      toast.error("Erreur déconnexion");
    }
  };

  // Filtrer les QR codes
  const filteredQRCodes = qrCodes.filter(
    (qr) =>
      qr.qrId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (qr.objectName &&
        qr.objectName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (qr.ownerEmail &&
        qr.ownerEmail.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const renderTabContent = () => {
    const salesStats = calculateSalesStats();

    switch (activeTab) {
      case "dashboard":
        const localStats = {
          totalQRCodes: qrCodes.length,
          assigned: qrCodes.filter((q) => q.status === "assigned").length,
          active: qrCodes.filter((q) => q.scanCount > 0).length,
          found: qrCodes.filter((q) => q.isFound).length,
        };
        return (
          <div className="admin-content">
            {/* Statistiques */}
            <div className="stats-grid">
              {/* Tags Vendus */}
              <div className="stat-card">
                <div className="stat-header">
                  <div className="stat-title">Tags Vendus</div>
                  <div className="stat-icon blue">
                    <Package size={24} />
                  </div>
                </div>
                <div className="stat-value2">{salesStats.totalTagsSold}</div>
                <div className="stat-trend">
                  {salesStats.completedSales > 0
                    ? `${Math.round(
                        salesStats.totalTagsSold / salesStats.completedSales
                      )} tags/vente`
                    : "0 tags/vente"}
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <div className="stat-title">Assignés</div>
                  <div className="stat-icon green">
                    <Package size={24} />
                  </div>
                </div>
                <div className="stat-value2">{localStats.assigned}</div>{" "}
                <div className="stat-trend positive">
            {localStats.totalQRCodes > 0
              ? `${Math.round(
                  (localStats.assigned / salesStats.totalTagsSold) * 100
                )}% utilisés`
              : "0%"}
          </div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <div className="stat-title">Actifs</div>
                  <div className="stat-icon orange">
                    <BarChart3 size={24} />
                  </div>
                </div>
                <div className="stat-value2">{localStats.active}</div> {/* CHANGÉ */}
          <div className="stat-trend">
            {localStats.assigned > 0
              ? `${Math.round(
                  (localStats.active / localStats.assigned) * 100
                )}% scannés`
              : "0%"}
          </div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <div className="stat-title">Objets trouvés</div>
                  <div className="stat-icon purple">
                    <Users size={24} />
                  </div>
                </div>
                <div className="stat-value2">{localStats.found}</div> {/* CHANGÉ */}
          <div className="stat-trend positive">
            {localStats.assigned > 0
              ? `${Math.round(
                  (localStats.found / localStats.assigned) * 100
                )}% succès`
              : "0%"}
          </div>
              </div>
            </div>

            {/* Génération de QR */}
            <div className="generation-card">
              <h3 className="generation-title">Générer des QR Codes</h3>
              <div className="generation-form">
                <div className="form-group">
                  <label className="form-label">Nombre de QR codes</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={batchSize}
                    onChange={(e) =>
                      setBatchSize(parseInt(e.target.value) || 1)
                    }
                    className="form-input"
                    placeholder="Ex: 10"
                  />
                </div>
                <button
                  onClick={handleGenerateQR}
                  disabled={generating}
                  className="generate-btn"
                >
                  {generating ? (
                    <>
                      <div className="spinner-small"></div>
                      Génération...
                    </>
                  ) : (
                    <>
                      <PlusCircle size={20} />
                      Générer
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* QR Codes récents */}
            <div className="data-table-container">
              <div className="table-header">
                <h3 className="table-title">QR Codes récents</h3>
                <button onClick={handleExport} className="export-btn">
                  <Download size={16} />
                  Exporter CSV
                </button>
              </div>

              {qrCodes.length === 0 ? (
                <div className="empty-state">
                  <QrCode size={48} />
                  <p>Aucun QR code généré</p>
                  <button
                    onClick={handleGenerateQR}
                    className="btn btn-primary"
                  >
                    Générer les premiers QR codes
                  </button>
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>ID QR</th>
                      <th>Statut</th>
                      <th>Assignation</th>
                      <th>Scans</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {qrCodes.slice(0, 10).map((qr) => {
                      const ObjectIcon =
                        OBJECT_TYPES.find((t) => t.id === qr.objectType)
                          ?.icon || Package;
                      return (
                        <tr key={qr.id}>
                          <td>
                            <div className="qr-id-cell">
                              <code className="qr-id">{qr.qrId}</code>
                              <small className="qr-date">
                                {qr.createdAt.toLocaleDateString("fr-FR")}
                              </small>
                            </div>
                          </td>
                          <td>
                            <span
                              className={`qr-status ${
                                qr.status === "assigned"
                                  ? "status-assigned"
                                  : "status-unassigned"
                              }`}
                            >
                              {qr.status === "assigned" ? "Assigné" : "Libre"}
                            </span>
                          </td>
                          <td>
                            {qr.status === "assigned" ? (
                              <div className="assigned-info">
                                <ObjectIcon size={14} />
                                <span className="object-name">
                                  {qr.objectName || "Objet"}
                                </span>
                                {qr.ownerEmail && (
                                  <small className="owner-email">
                                    {qr.ownerEmail}
                                  </small>
                                )}
                              </div>
                            ) : (
                              <span className="not-assigned">
                                En attente d'assignation
                              </span>
                            )}
                          </td>
                          <td>
                            <div className="scan-info">
                              <div className="scan-count">
                                <Eye size={14} />
                                {qr.scanCount || 0}
                              </div>
                              {qr.firstScan && (
                                <small className="first-scan">
                                  Dernier:{" "}
                                  {qr.lastScan.toLocaleDateString("fr-FR")}
                                </small>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="action-buttons">
                              {qr.status === "unassigned" ? (
                                <button
                                  onClick={() => handleViewAssignPage(qr.qrId)}
                                  className="action-btn primary"
                                  title="Lien d'assignation"
                                >
                                  <Key size={16} />
                                  Assigner
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleViewScanPage(qr.qrId)}
                                  className="action-btn secondary"
                                  title="Scanner"
                                >
                                  <Eye size={16} />
                                  Scanner
                                </button>
                              )}
                              {qr.status === "assigned" && (
                                <button
                                  onClick={() => handleResetQR(qr.id)}
                                  className="action-btn danger"
                                  title="Réinitialiser"
                                >
                                  Réinitialiser
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        );

      case "qr-codes":
        // Filtrer les QR codes selon le statut sélectionné
        const filteredQRCodes = qrCodes.filter((qr) => {
          // Filtre par recherche textuelle
          const matchesSearch =
            qr.qrId.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (qr.objectName &&
              qr.objectName.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (qr.ownerEmail &&
              qr.ownerEmail.toLowerCase().includes(searchTerm.toLowerCase()));

          if (!matchesSearch) return false;

          // Filtre par statut
          if (qrFilterStatus === "assigned") return qr.status === "assigned";
          if (qrFilterStatus === "unassigned")
            return qr.status === "unassigned";
          if (qrFilterStatus === "scanned") return qr.scanCount > 0;
          return true; // "all"
        });

        // Pagination
        const indexOfLastQr = currentQrPage * qrItemsPerPage;
        const indexOfFirstQr = indexOfLastQr - qrItemsPerPage;
        const currentQRCodes = filteredQRCodes.slice(
          indexOfFirstQr,
          indexOfLastQr
        );
        const totalQrPages = Math.ceil(filteredQRCodes.length / qrItemsPerPage);

        return (
          <div className="admin-content">
            <div className="qr-codes-section">
              <div className="section-header">
                <div className="tete">
                  <h3 className="section-title">Tous les QR Codes</h3>
                  <div className="section-controls">
                    <div className="search-container">
                      <Search className="search-icon" size={18} />
                      <input
                        type="text"
                        placeholder="Rechercher par ID, objet ou email..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentQrPage(1); // Reset à la page 1 lors d'une recherche
                        }}
                        className="search-input"
                      />
                    </div>

                    {/* Bouton export CSV existant */}
                    <button onClick={handleExport} className="export-btn">
                      <Download size={16} />
                      Exporter CSV
                    </button>

                    {/* Bouton export stickers PDF */}
                    <button
                      onClick={handleExportStickers}
                      className="export-pdf-btn"
                      style={{
                        backgroundColor: "#dc2626",
                        color: "white",
                        border: "none",
                        padding: "8px 16px",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                        fontWeight: "500",
                        marginLeft: "10px",
                      }}
                      disabled={
                        qrCodes.filter((q) => q.status === "unassigned")
                          .length === 0
                      }
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      Exporter stickers PDF
                      <span
                        style={{
                          background: "rgba(255,255,255,0.2)",
                          borderRadius: "12px",
                          padding: "2px 8px",
                          fontSize: "12px",
                          marginLeft: "5px",
                        }}
                      >
                        {
                          qrCodes.filter((q) => q.status === "unassigned")
                            .length
                        }
                      </span>
                    </button>
                  </div>
                </div>

                {/* Filtres par statut */}
                <div className="filter-buttons">
                  <button
                    onClick={() => {
                      setQrFilterStatus("all");
                      setCurrentQrPage(1);
                    }}
                    className={`filter-btn ${
                      qrFilterStatus === "all" ? "active" : ""
                    }`}
                    style={{
                      backgroundColor:
                        qrFilterStatus === "all" ? "#3b82f6" : "#f3f4f6",
                      color: qrFilterStatus === "all" ? "white" : "#374151",
                      border: "none",
                      padding: "8px 12px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      marginRight: "5px",
                    }}
                  >
                    Tous ({qrCodes.length})
                  </button>
                  <button
                    onClick={() => {
                      setQrFilterStatus("assigned");
                      setCurrentQrPage(1);
                    }}
                    className={`filter-btn ${
                      qrFilterStatus === "assigned" ? "active" : ""
                    }`}
                    style={{
                      backgroundColor:
                        qrFilterStatus === "assigned" ? "#3b82f6" : "#f3f4f6",
                      color:
                        qrFilterStatus === "assigned" ? "white" : "#374151",
                      border: "none",
                      padding: "8px 12px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      marginRight: "5px",
                    }}
                  >
                    Assignés (
                    {qrCodes.filter((q) => q.status === "assigned").length})
                  </button>
                  <button
                    onClick={() => {
                      setQrFilterStatus("unassigned");
                      setCurrentQrPage(1);
                    }}
                    className={`filter-btn ${
                      qrFilterStatus === "unassigned" ? "active" : ""
                    }`}
                    style={{
                      backgroundColor:
                        qrFilterStatus === "unassigned" ? "#3b82f6" : "#f3f4f6",
                      color:
                        qrFilterStatus === "unassigned" ? "white" : "#374151",
                      border: "none",
                      padding: "8px 12px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      marginRight: "5px",
                    }}
                  >
                    Non assignés (
                    {qrCodes.filter((q) => q.status === "unassigned").length})
                  </button>
                  <button
                    onClick={() => {
                      setQrFilterStatus("scanned");
                      setCurrentQrPage(1);
                    }}
                    className={`filter-btn ${
                      qrFilterStatus === "scanned" ? "active" : ""
                    }`}
                    style={{
                      backgroundColor:
                        qrFilterStatus === "scanned" ? "#3b82f6" : "#f3f4f6",
                      color: qrFilterStatus === "scanned" ? "white" : "#374151",
                      border: "none",
                      padding: "8px 12px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      marginRight: "5px",
                    }}
                  >
                    Scannés ({qrCodes.filter((q) => q.scanCount > 0).length})
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="loading-table">
                  <div className="spinner"></div>
                  <p>Chargement des QR codes...</p>
                </div>
              ) : filteredQRCodes.length === 0 ? (
                <div className="empty-state">
                  <Search size={48} />
                  <p>Aucun QR code trouvé</p>
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="btn btn-outline"
                    >
                      Effacer la recherche
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="qr-codes-grid">
                    {currentQRCodes.map((qr) => {
                      const ObjectIcon =
                        OBJECT_TYPES.find((t) => t.id === qr.objectType)
                          ?.icon || QrCode;
                      return (
                        <div key={qr.id} className="qr-code-card">
                          <div className="qr-display">
                            <QRCode
                              value={`${window.location.origin}/scan/${qr.qrId}`}
                              size={120}
                              bgColor="#FFFFFF"
                              fgColor="#0066FF"
                            />
                          </div>
                          <div className="qr-info">
                            <div className="qr-id">{qr.qrId}</div>
                            <div
                              className={`qr-status ${
                                qr.status === "assigned"
                                  ? "status-assigned"
                                  : "status-unassigned"
                              }`}
                            >
                              {qr.status === "assigned" ? "Assigné" : "Libre"}
                            </div>
                            {qr.objectName ? (
                              <div className="qr-object">
                                <ObjectIcon size={14} />
                                {qr.objectName}
                              </div>
                            ) : (
                              <div className="qr-object pending">
                                <QrCode size={14} />
                                En attente d'assignation
                              </div>
                            )}
                            <div className="qr-meta">
                              <div className="qr-date">
                                <Calendar size={14} />
                                {qr.createdAt.toLocaleDateString("fr-FR")}
                              </div>
                              {qr.scanCount > 0 && (
                                <div className="qr-scans">
                                  <Eye size={14} />
                                  {qr.scanCount} scan
                                  {qr.scanCount > 1 ? "s" : ""}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="qr-actions">
                            {qr.status === "unassigned" ? (
                              <button
                                onClick={() => handleViewAssignPage(qr.qrId)}
                                className="qr-action-btn primary"
                              >
                                <Key size={16} />
                                Assigner
                              </button>
                            ) : (
                              <button
                                onClick={() => handleViewScanPage(qr.qrId)}
                                className="qr-action-btn secondary"
                              >
                                <Eye size={16} />
                                Scanner
                              </button>
                            )}
                            <button
                              onClick={() => handleViewQRDetail(qr)}
                              className="qr-action-btn outline"
                            >
                              Détails
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination pour QR codes */}
                  {totalQrPages > 1 && (
                    <div className="pagination">
                      <button
                        onClick={() =>
                          setCurrentQrPage((prev) => Math.max(prev - 1, 1))
                        }
                        disabled={currentQrPage === 1}
                        className="pagination-btn"
                        style={{
                          backgroundColor: "#3b82f6",
                          color: "white",
                          border: "none",
                          padding: "8px 12px",
                          borderRadius: "6px",
                          cursor:
                            currentQrPage === 1 ? "not-allowed" : "pointer",
                          opacity: currentQrPage === 1 ? 0.5 : 1,
                          margin: "0 5px",
                        }}
                      >
                        Précédent
                      </button>

                      {Array.from(
                        { length: Math.min(5, totalQrPages) },
                        (_, i) => {
                          let pageNum;
                          if (totalQrPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentQrPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentQrPage >= totalQrPages - 2) {
                            pageNum = totalQrPages - 4 + i;
                          } else {
                            pageNum = currentQrPage - 2 + i;
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentQrPage(pageNum)}
                              className="pagination-btn"
                              style={{
                                backgroundColor:
                                  currentQrPage === pageNum
                                    ? "#3b82f6"
                                    : "#f3f4f6",
                                color:
                                  currentQrPage === pageNum
                                    ? "white"
                                    : "#374151",
                                border: "none",
                                padding: "8px 12px",
                                borderRadius: "6px",
                                cursor: "pointer",
                                margin: "0 3px",
                                minWidth: "40px",
                              }}
                            >
                              {pageNum}
                            </button>
                          );
                        }
                      )}

                      <button
                        onClick={() =>
                          setCurrentQrPage((prev) =>
                            Math.min(prev + 1, totalQrPages)
                          )
                        }
                        disabled={currentQrPage === totalQrPages}
                        className="pagination-btn"
                        style={{
                          backgroundColor: "#3b82f6",
                          color: "white",
                          border: "none",
                          padding: "8px 12px",
                          borderRadius: "6px",
                          cursor:
                            currentQrPage === totalQrPages
                              ? "not-allowed"
                              : "pointer",
                          opacity: currentQrPage === totalQrPages ? 0.5 : 1,
                          margin: "0 5px",
                        }}
                      >
                        Suivant
                      </button>

                      <span
                        className="pagination-info"
                        style={{ marginLeft: "15px", color: "#6b7280" }}
                      >
                        Page {currentQrPage} sur {totalQrPages} (
                        {filteredQRCodes.length} QR codes)
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );

      case "sales":
        return (
          <div className="admin-content">
            {/* Statistiques rapides */}
            <div className="stats-grid">
              {/* Revenu Total */}
              <div className="stat-card">
                <div className="stat-header">
                  <div className="stat-title">Revenu Total</div>
                  <div className="stat-icon green">
                    <DollarSign size={24} />
                  </div>
                </div>
                <div className="stat-value2">
                  {salesStats.totalRevenue.toLocaleString("fr-FR")} CFA
                </div>
                <div className="stat-trend positive">
                  <TrendingUp size={16} />
                  {salesStats.monthlyRevenue.toLocaleString("fr-FR")} CFA ce
                  mois
                </div>
              </div>

              {/* Ventes Complétées */}
              <div className="stat-card">
                <div className="stat-header">
                  <div className="stat-title">Ventes Complétées</div>
                  <div className="stat-icon purple">
                    <CheckCircle size={24} />
                  </div>
                </div>
                <div className="stat-value2">{salesStats.completedSales}</div>
                <div className="stat-trend">
                  {salesStats.totalSales > 0
                    ? `${salesStats.conversionRate}% de réussite`
                    : "Aucune vente"}
                </div>
              </div>

              {/* Ventes en Attente */}
              <div className="stat-card">
                <div className="stat-header">
                  <div className="stat-title">En Attente</div>
                  <div className="stat-icon orange">
                    <Clock size={24} />{" "}
                    {/* Ajoutez Clock à vos imports lucide-react */}
                  </div>
                </div>
                <div className="stat-value2">
                  {salesStats.pendingSalesCount}
                </div>
                <div className="stat-trend">
                  {salesStats.pendingRevenue > 0
                    ? `${salesStats.pendingRevenue.toLocaleString("fr-FR")} CFA`
                    : "Aucun montant"}
                </div>
              </div>

              {/* Tags Vendus */}
              <div className="stat-card">
                <div className="stat-header">
                  <div className="stat-title">Tags Vendus</div>
                  <div className="stat-icon blue">
                    <Package size={24} />
                  </div>
                </div>
                <div className="stat-value2">{salesStats.totalTagsSold}</div>
                <div className="stat-trend">
                  {salesStats.completedSales > 0
                    ? `${Math.round(
                        salesStats.totalTagsSold / salesStats.completedSales
                      )} tags/vente`
                    : "0 tags/vente"}
                </div>
              </div>
            </div>

            {/* Contrôles */}
            <div className="controls-card">
              <div className="filters-row">
                <div className="filter-group">
                  <label className="filter-label">
                    <Calendar size={16} />
                    Date début
                  </label>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        startDate: e.target.value,
                      }))
                    }
                    className="filter-input"
                  />
                </div>

                <div className="filter-group">
                  <label className="filter-label">
                    <Calendar size={16} />
                    Date fin
                  </label>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        endDate: e.target.value,
                      }))
                    }
                    className="filter-input"
                  />
                </div>

                <div className="filter-group">
                  <label className="filter-label">
                    <CreditCard size={16} />
                    Paiement
                  </label>
                  <select
                    value={filters.paymentMethod}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        paymentMethod: e.target.value,
                      }))
                    }
                    className="filter-select"
                  >
                    <option value="all">Tous</option>
                    <option value="cinetpay">CinetPay</option>
                    <option value="cash">Espèces</option>
                    <option value="transfer">Virement</option>
                    <option value="other">Autre</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label className="filter-label">
                    <CheckCircle size={16} />
                    Statut
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        status: e.target.value,
                      }))
                    }
                    className="filter-select"
                  >
                    <option value="all">Tous</option>
                    <option value="completed">Complété</option>
                    <option value="pending">En attente</option>
                    <option value="failed">Échoué</option>
                    <option value="refunded">Remboursé</option>
                  </select>
                </div>

                <button
                  onClick={() =>
                    setFilters({
                      startDate: "",
                      endDate: "",
                      paymentMethod: "all",
                      status: "all",
                    })
                  }
                  className="clear-filters-btn"
                >
                  <RefreshCw size={16} />
                  Réinitialiser
                </button>

                <button
                  onClick={() => setShowAddSaleForm(true)}
                  className="add-sale-btn"
                >
                  <UserPlus size={16} />
                  Ajouter une vente
                </button>
              </div>
            </div>

            {/* Statistiques détaillées par pack */}
            <div className="packages-stats">
              <h3 className="section-title">Statistiques par Pack</h3>
              <div className="packages-grid">
                {salesStats.packageStats.map((pkg) => (
                  <div key={pkg.id} className="package-stat-card">
                    <div className="package-header">
                      <h4 className="package-name">{pkg.name}</h4>
                      <div className="package-price">
                        {pkg.price > 0
                          ? `${pkg.price.toLocaleString("fr-FR")} CFA`
                          : "Personnalisé"}
                      </div>
                    </div>
                    <div className="package-details">
                      <div className="package-stat">
                        <span className="stat-label">Ventes:</span>
                        <span className="stat-value">{pkg.count}</span>
                      </div>
                      <div className="package-stat">
                        <span className="stat-label">Tags vendus:</span>
                        <span className="stat-value">{pkg.tagsSold}</span>
                      </div>
                      <div className="package-stat">
                        <span className="stat-label">Revenu:</span>
                        <span className="stat-value revenue">
                          {pkg.revenue.toLocaleString("fr-FR")} CFA
                        </span>
                      </div>
                    </div>
                    {pkg.count > 0 && (
                      <div className="package-percentage">
                        {Math.round((pkg.count / salesStats.totalSales) * 100)}%
                        des ventes
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Liste des ventes */}
            <div className="sales-table-container">
              <div className="table-header">
                <h3 className="table-title">Liste des Ventes</h3>

                <div className="summary-info">
                  {filteredSales.length} ventes · {salesStats.totalTagsSold}{" "}
                  tags · {salesStats.totalRevenue.toLocaleString("fr-FR")} CFA
                </div>
              </div>

              <button className="verified" onClick={verifyPendingPayments}>
                Vérifier paiement
              </button>

              {/* Pagination pour les ventes */}
              {loadingSales ? (
                <div className="loading-table">
                  <div className="spinner"></div>
                  <p>Chargement des ventes...</p>
                </div>
              ) : filteredSales.length === 0 ? (
                <div className="empty-state">
                  <ShoppingBag size={48} />
                  <p>Aucune vente trouvée</p>
                  <button
                    onClick={() => setShowAddSaleForm(true)}
                    className="btn btn-primary"
                  >
                    Ajouter la première vente
                  </button>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>ID Transaction</th>
                          <th>Client</th>
                          <th>Pack</th>
                          <th>Montant</th>
                          <th>Paiement</th>
                          <th>Statut</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSales
                          .slice(
                            (currentSalesPage - 1) * salesItemsPerPage,
                            currentSalesPage * salesItemsPerPage
                          )
                          .map((sale) => {
                            const pkg = PACKAGES.find(
                              (p) => p.id === sale.package
                            );
                            return (
                              <tr
                                key={sale.id}
                                onClick={() => handleViewSaleDetail(sale)}
                                style={{ cursor: "pointer" }}
                                className="clickable-row"
                              >
                                <td>
                                  <code className="transaction-id">
                                    {sale.transactionId}
                                  </code>
                                </td>
                                <td>
                                  <div className="customer-info">
                                    <div className="customer-name">
                                      {sale.customerName}
                                    </div>
                                    {sale.customerEmail && (
                                      <small className="customer-email">
                                        {sale.customerEmail}
                                      </small>
                                    )}
                                    {sale.customerPhone && (
                                      <small className="customer-phone">
                                        {sale.customerPhone}
                                      </small>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div className="package-info">
                                    <span className="package-badge">
                                      {sale?.packageName || "Personnalisé"}
                                    </span>
                                    <small>{sale.tagsCount || 0} tags</small>
                                  </div>
                                </td>
                                <td>
                                  <div className="amount-cell">
                                    <strong className="amount">
                                      {sale.amount.toLocaleString("fr-FR")} CFA
                                    </strong>
                                  </div>
                                </td>
                                <td>
                                  <div
                                    className={`payment-method ${sale.paymentMethod}`}
                                  >
                                    {sale.paymentMethod === "mobile"
                                      ? "CinetPay"
                                      : sale.paymentMethod === "cash"
                                      ? "Espèces"
                                      : sale.paymentMethod === "transfer"
                                      ? "Virement"
                                      : "Autre"}
                                  </div>
                                </td>
                                <td>
                                  <div
                                    className={`sale-status status-${sale.status}`}
                                  >
                                    {sale.status === "completed"
                                      ? "Complété"
                                      : sale.status === "pending"
                                      ? "En attente"
                                      : sale.status === "failed"
                                      ? "Échoué"
                                      : "Annulé"}
                                  </div>
                                </td>
                                <td>
                                  {sale.createdAt.toLocaleDateString("fr-FR")}
                                  <br />
                                  <small>
                                    {sale.createdAt.toLocaleTimeString("fr-FR")}
                                  </small>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination pour les ventes */}
                  {filteredSales.length > salesItemsPerPage && (
                    <div
                      className="pagination"
                      style={{
                        marginTop: "20px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <button
                        onClick={() =>
                          setCurrentSalesPage((prev) => Math.max(prev - 1, 1))
                        }
                        disabled={currentSalesPage === 1}
                        className="pagination-btn"
                        style={{
                          backgroundColor: "#3b82f6",
                          color: "white",
                          border: "none",
                          padding: "8px 16px",
                          borderRadius: "6px",
                          cursor:
                            currentSalesPage === 1 ? "not-allowed" : "pointer",
                          opacity: currentSalesPage === 1 ? 0.5 : 1,
                          margin: "0 5px",
                        }}
                      >
                        Précédent
                      </button>

                      {Array.from(
                        {
                          length: Math.min(
                            5,
                            Math.ceil(filteredSales.length / salesItemsPerPage)
                          ),
                        },
                        (_, i) => {
                          const totalPages = Math.ceil(
                            filteredSales.length / salesItemsPerPage
                          );
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentSalesPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentSalesPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentSalesPage - 2 + i;
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentSalesPage(pageNum)}
                              className="pagination-btn"
                              style={{
                                backgroundColor:
                                  currentSalesPage === pageNum
                                    ? "#3b82f6"
                                    : "#f3f4f6",
                                color:
                                  currentSalesPage === pageNum
                                    ? "white"
                                    : "#374151",
                                border: "none",
                                padding: "8px 12px",
                                borderRadius: "6px",
                                cursor: "pointer",
                                margin: "0 3px",
                                minWidth: "40px",
                              }}
                            >
                              {pageNum}
                            </button>
                          );
                        }
                      )}

                      <button
                        onClick={() =>
                          setCurrentSalesPage((prev) =>
                            Math.min(
                              prev + 1,
                              Math.ceil(
                                filteredSales.length / salesItemsPerPage
                              )
                            )
                          )
                        }
                        disabled={
                          currentSalesPage ===
                          Math.ceil(filteredSales.length / salesItemsPerPage)
                        }
                        className="pagination-btn"
                        style={{
                          backgroundColor: "#3b82f6",
                          color: "white",
                          border: "none",
                          padding: "8px 16px",
                          borderRadius: "6px",
                          cursor:
                            currentSalesPage ===
                            Math.ceil(filteredSales.length / salesItemsPerPage)
                              ? "not-allowed"
                              : "pointer",
                          opacity:
                            currentSalesPage ===
                            Math.ceil(filteredSales.length / salesItemsPerPage)
                              ? 0.5
                              : 1,
                          margin: "0 5px",
                        }}
                      >
                        Suivant
                      </button>

                      <span
                        className="pagination-info"
                        style={{ marginLeft: "15px", color: "#6b7280" }}
                      >
                        Page {currentSalesPage} sur{" "}
                        {Math.ceil(filteredSales.length / salesItemsPerPage)} (
                        {filteredSales.length} ventes)
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Modal d'ajout de vente */}
            {showAddSaleForm && (
              <div className="modal-overlay">
                <div className="modal-content large">
                  {" "}
                  {/* Ajoutez la classe "large" pour plus d'espace */}
                  <div className="modal-header">
                    <h3>Ajouter une Vente</h3>
                    <button
                      onClick={() => {
                        setShowAddSaleForm(false);
                        resetSaleForm();
                      }}
                      className="modal-close"
                    >
                      ×
                    </button>
                  </div>
                  <div className="modal-body">
                    <div className="form-grid">
                      {/* Section Client */}
                      <h4 className="form-section-title">
                        Informations client
                      </h4>

                      <div className="form-group">
                        <label className="form-label">Nom du client *</label>
                        <input
                          type="text"
                          value={saleForm.customerName}
                          onChange={(e) =>
                            setSaleForm((prev) => ({
                              ...prev,
                              customerName: e.target.value,
                            }))
                          }
                          className="form-input"
                          placeholder="Nom complet"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Email</label>
                        <input
                          type="email"
                          value={saleForm.customerEmail}
                          onChange={(e) =>
                            setSaleForm((prev) => ({
                              ...prev,
                              customerEmail: e.target.value,
                            }))
                          }
                          className="form-input"
                          placeholder="email@exemple.com"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Téléphone</label>
                        <input
                          type="tel"
                          value={saleForm.customerPhone}
                          onChange={(e) =>
                            setSaleForm((prev) => ({
                              ...prev,
                              customerPhone: e.target.value,
                            }))
                          }
                          className="form-input"
                          placeholder="+225 XX XX XX XX"
                        />
                      </div>

                      {/* Section Livraison - NOUVEAU */}
                      <h4 className="form-section-title">
                        Adresse de livraison
                      </h4>

                      <div className="form-group">
                        <label className="form-label">Ville *</label>
                        <select
                          value={saleForm.deliveryCity || ""}
                          onChange={(e) =>
                            setSaleForm((prev) => ({
                              ...prev,
                              deliveryCity: e.target.value,
                              // Mettre à jour automatiquement les frais selon la ville
                              deliveryCost:
                                e.target.value === "Abidjan"
                                  ? 1000
                                  : e.target.value === "Yamoussoukro"
                                  ? 2000
                                  : e.target.value === "Bouaké"
                                  ? 2500
                                  : e.target.value === "Daloa"
                                  ? 2500
                                  : e.target.value === "Korhogo"
                                  ? 3000
                                  : 3500,
                            }))
                          }
                          className="form-select"
                        >
                          <option value="">Sélectionnez une ville</option>
                          <option value="Abidjan">Abidjan (1000 CFA)</option>
                          <option value="Yamoussoukro">
                            Yamoussoukro (2000 CFA)
                          </option>
                          <option value="Bouaké">Bouaké (2500 CFA)</option>
                          <option value="Daloa">Daloa (2500 CFA)</option>
                          <option value="Korhogo">Korhogo (3000 CFA)</option>
                          <option value="Autre">Autre ville (3500 CFA)</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Quartier/Zone *</label>
                        <input
                          type="text"
                          value={saleForm.deliveryZone || ""}
                          onChange={(e) =>
                            setSaleForm((prev) => ({
                              ...prev,
                              deliveryZone: e.target.value,
                            }))
                          }
                          className="form-input"
                          placeholder="Ex: Cocody, Marcory, Plateau..."
                        />
                      </div>

                      <div className="form-group full-width">
                        <label className="form-label">
                          Adresse détaillée *
                        </label>
                        <textarea
                          value={saleForm.deliveryAddress || ""}
                          onChange={(e) =>
                            setSaleForm((prev) => ({
                              ...prev,
                              deliveryAddress: e.target.value,
                            }))
                          }
                          className="form-textarea"
                          placeholder="Rue, avenue, immeuble, étage, repères..."
                          rows="2"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          Frais de livraison (CFA)
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={saleForm.deliveryCost || 0}
                          onChange={(e) =>
                            setSaleForm((prev) => ({
                              ...prev,
                              deliveryCost: parseInt(e.target.value) || 0,
                            }))
                          }
                          className="form-input"
                        />
                        <small className="form-help">
                          Automatique selon la ville, modifiable si besoin
                        </small>
                      </div>

                      <div className="form-group full-width">
                        <label className="form-label">
                          Instructions de livraison
                        </label>
                        <textarea
                          value={saleForm.deliveryNotes || ""}
                          onChange={(e) =>
                            setSaleForm((prev) => ({
                              ...prev,
                              deliveryNotes: e.target.value,
                            }))
                          }
                          className="form-textarea"
                          placeholder="Ex: Livrer après 18h, Appeler avant, Code portail..."
                          rows="2"
                        />
                      </div>

                      {/* Section Pack */}
                      <h4 className="form-section-title">
                        Détails de la commande
                      </h4>

                      <div className="form-group">
                        <label className="form-label">Pack *</label>
                        <select
                          value={saleForm.package}
                          onChange={(e) => handlePackageChange(e.target.value)}
                          className="form-select"
                        >
                          {PACKAGES.map((pkg) => (
                            <option key={pkg.id} value={pkg.id}>
                              {pkg.name}{" "}
                              {pkg.price > 0
                                ? `(${pkg.price.toLocaleString(
                                    "fr-FR"
                                  )} CFA - ${pkg.tags} tags)`
                                : ""}
                            </option>
                          ))}
                        </select>
                      </div>

                      {saleForm.package === "custom" && (
                        <div className="form-group">
                          <label className="form-label">Nombre de tags *</label>
                          <input
                            type="number"
                            min="1"
                            value={saleForm.customTags}
                            onChange={(e) => {
                              const tags = parseInt(e.target.value) || 1;
                              setSaleForm((prev) => ({
                                ...prev,
                                customTags: tags,
                                amount: tags * 1000, // Prix par tag (ajustable)
                              }));
                            }}
                            className="form-input"
                          />
                        </div>
                      )}

                      <div className="form-group">
                        <label className="form-label">Montant (CFA) *</label>
                        <input
                          type="number"
                          min="0"
                          value={saleForm.amount}
                          onChange={(e) =>
                            setSaleForm((prev) => ({
                              ...prev,
                              amount: parseFloat(e.target.value) || 0,
                            }))
                          }
                          className="form-input"
                        />
                      </div>

                      {/* Section Paiement */}
                      <h4 className="form-section-title">Paiement</h4>

                      <div className="form-group">
                        <label className="form-label">Mode de paiement *</label>
                        <select
                          value={saleForm.paymentMethod}
                          onChange={(e) =>
                            setSaleForm((prev) => ({
                              ...prev,
                              paymentMethod: e.target.value,
                            }))
                          }
                          className="form-select"
                        >
                          <option value="cinetpay">CinetPay</option>
                          <option value="cash">Espèces</option>
                          <option value="mobile">Mobile Money</option>
                          <option value="transfer">Virement</option>
                          <option value="other">Autre</option>
                        </select>
                      </div>

                      {saleForm.paymentMethod === "cinetpay" && (
                        <div className="form-group">
                          <label className="form-label">
                            ID Transaction CinetPay
                          </label>
                          <input
                            type="text"
                            value={saleForm.cinetpayId}
                            onChange={(e) =>
                              setSaleForm((prev) => ({
                                ...prev,
                                cinetpayId: e.target.value,
                              }))
                            }
                            className="form-input"
                            placeholder="CP-XXXX-XXXX"
                          />
                        </div>
                      )}

                      <div className="form-group">
                        <label className="form-label">Statut *</label>
                        <select
                          value={saleForm.status}
                          onChange={(e) =>
                            setSaleForm((prev) => ({
                              ...prev,
                              status: e.target.value,
                            }))
                          }
                          className="form-select"
                        >
                          <option value="completed">Complété</option>
                          <option value="pending">En attente</option>
                          <option value="failed">Échoué</option>
                          <option value="refunded">Remboursé</option>
                        </select>
                      </div>

                      <div className="form-group full-width">
                        <label className="form-label">Notes</label>
                        <textarea
                          value={saleForm.notes}
                          onChange={(e) =>
                            setSaleForm((prev) => ({
                              ...prev,
                              notes: e.target.value,
                            }))
                          }
                          className="form-textarea"
                          placeholder="Notes additionnelles..."
                          rows="3"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      onClick={() => {
                        setShowAddSaleForm(false);
                        resetSaleForm();
                      }}
                      className="btn btn-outline"
                    >
                      Annuler
                    </button>
                    <button onClick={handleAddSale} className="btn btn-primary">
                      Enregistrer la vente
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case "settings":
        return (
          <div className="admin-content">
            <div className="settings-card">
              <h3 className="settings-title">Paramètres Admin</h3>
              <div className="settings-info">
                <div className="info-item">
                  <strong>Utilisateur:</strong> {user?.email}
                </div>
                <div className="info-item">
                  <strong>Rôle:</strong> Administrateur
                </div>
                <div className="info-item">
                  <strong>Total QR codes:</strong> {stats.totalQRCodes}
                </div>
                <div className="info-item">
                  <strong>Assignés:</strong> {stats.assigned} (
                  {stats.totalQRCodes > 0
                    ? Math.round(
                        (stats.assigned / salesStats.totalTagsSold) * 100
                      )
                    : 0}
                  %)
                </div>
              </div>

              <div className="url-info">
                <h4>URLs importantes</h4>
                <div className="url-list">
                  <div className="url-item">
                    <strong>Assignation:</strong> /assign/[qrId]
                  </div>
                  <div className="url-item">
                    <strong>Scan public:</strong> /scan/[qrId]
                  </div>
                  <div className="url-item">
                    <strong>Dashboard admin:</strong> /admin
                  </div>
                </div>
              </div>

              <div className="danger-zone">
                <h4>Zone de danger</h4>
                <p className="danger-warning">Ces actions sont irréversibles</p>
                <button onClick={handleLogout} className="danger-btn logout">
                  <LogOut size={16} />
                  Déconnexion
                </button>
              </div>
            </div>
          </div>
        );
    }
  };

  if (loading && qrCodes.length === 0) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <p className="loading-text">Connexion à Firebase...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-layout">
        {/* Sidebar */}
        <aside className="admin-sidebar">
          <div className="sidebar-header">
            <div className="sidebar-brand">
              <div className="brand-logo">
                <QrCode size={24} />
              </div>
              <div className="brand-text">UltimaTag</div>
            </div>
            <div className="sidebar-subtitle">Gestion des QR codes</div>
          </div>

          <nav className="sidebar-nav">
            <button
              onClick={() => setActiveTab("dashboard")}
              className={`nav-item ${
                activeTab === "dashboard" ? "active" : ""
              }`}
            >
              <BarChart3 className="nav-icon" />
              <span className="nav-label">Tableau de bord</span>
              {qrCodes.length > 0 && (
                <span className="nav-badge">{qrCodes.length}</span>
              )}
            </button>

            <button
              onClick={() => setActiveTab("qr-codes")}
              className={`nav-item ${activeTab === "qr-codes" ? "active" : ""}`}
            >
              <QrCode className="nav-icon" />
              <span className="nav-label">QR Codes</span>
            </button>

            <button
              onClick={() => setActiveTab("sales")}
              className={`nav-item ${activeTab === "sales" ? "active" : ""}`}
            >
              <DollarSign className="nav-icon" />
              <span className="nav-label">Ventes</span>
              {sales.length > 0 && (
                <span className="nav-badge">{sales.length}</span>
              )}
            </button>

            <button
              onClick={() => setActiveTab("settings")}
              className={`nav-item ${activeTab === "settings" ? "active" : ""}`}
            >
              <Settings className="nav-icon" />
              <span className="nav-label">Paramètres</span>
            </button>

            <button onClick={() => navigate("/")} className="nav-item">
              <Home className="nav-icon" />
              <span className="nav-label">Retour au site public</span>
            </button>
          </nav>

          <div className="sidebar-footer">
            <div className="user-info">
              <div className="user-avatar">
                {user?.email?.charAt(0).toUpperCase()}
              </div>
              <div className="user-details">
                <div className="user-name">{user?.email?.split("@")[0]}</div>
                <div className="user-email">{user?.email}</div>
                <div className="user-stats">
                  {stats.assigned} / {stats.totalQRCodes} assignés
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="admin-main">
          <header className="admin-header">
            <div className="header-left">
              <h1>Administration UltimaTag</h1>
              <p className="header-subtitle">
                Générez et gérez les QR codes pour vos clients
              </p>
            </div>

            <div className="header-actions">
              <div className="header-time">
                {new Date().toLocaleDateString("fr-FR", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
              <div className="header-stats">
  <div className="stat-badge primary">
    <QrCode size={14} />
    {qrCodes.length} total {/* Utiliser qrCodes.length directement */}
  </div>
  <div className="stat-badge success">
    <Package size={14} />
    {qrCodes.filter(q => q.status === "assigned").length} assignés {/* Filtrer localement */}
  </div>
  <div className="stat-badge info">
    <Eye size={14} />
    {qrCodes.filter(q => q.scanCount > 0).length} actifs {/* Filtrer localement */}
  </div>
</div>
            </div>
          </header>

          {renderTabContent()}
        </main>
      </div>
      {showSaleDetailModal && renderSaleDetailModal()}{" "}
      {/* Ajoutez cette ligne */}
      {showQRDetailModal && renderQRDetailModal()}
      {/* Modals */}
    </div>
  );
}
